import { useEffect, useMemo, useState } from "react";
import "./ReportsSectionCompact.css";
import { useNavigate } from "react-router-dom";
import ModalCloseX from "../components/common/ModalCloseX";
import InfoTooltip from "../components/common/InfoTooltip";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import { useAuth } from "../context/AuthContext";
import {
  fetchSuperAdminReportFilters,
  fetchSuperAdminReportKpis,
} from "../api/superadmin";
import {
  buildSuperAdminReportHelpGuide,
  getSuperAdminHelpAnchors,
} from "../constants/superAdminReportHelpGuides";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";

const SCOPE_OPTIONS = [
  { value: "all", label: "All Salons" },
  { value: "business", label: "Business" },
  { value: "salon", label: "Salon" },
];

const PRESET_OPTIONS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "this_year", label: "This Year" },
  { value: "custom", label: "Custom" },
];

const GROUP_BY_OPTIONS = [
  { value: "salon", label: "Salon" },
  { value: "day", label: "Day" },
];

const DEFAULT_FILTERS = {
  scope: "all",
  business_id: "",
  salon_id: "",
  preset: "today",
  start_date: "",
  end_date: "",
  group_by: "salon",
  include_refunds: true,
  include_voided: false,
};

const KPI_CARDS = [
  { key: "ticket_count", label: "Ticket Count", type: "count" },
  { key: "gross_sales", label: "Gross Sales", type: "money" },
  { key: "refund_amount", label: "Refund Amount", type: "money" },
  { key: "net_sales", label: "Net Sales", type: "money" },
  {
    key: "revenue_per_booked_minute",
    label: "Revenue / Booked Min",
    type: "money_per_minute",
  },
  { key: "tips_total", label: "Tips Total", type: "money" },
  { key: "avg_ticket", label: "Avg Ticket", type: "money" },
  { key: "tax_total", label: "Tax Total", type: "money" },
  { key: "discount_total", label: "Discount Total", type: "money" },
];

const KPI_TOOLTIPS = {
  scope:
    "Choose All Salons, Business, or Salon scope. Date filters always use each salon's local accounting date.",
  business:
    "Choose a business when running Business scope. Salon list is filtered to this business.",
  salon:
    "Choose a salon when running Salon scope. Grouping is forced to Day for single-salon detail.",
  preset:
    "Preset ranges are evaluated in each salon's local date instead of browser or UTC date.",
  group_by:
    "Choose Salon rollup or Day trend view. Salon scope always uses Day grouping.",
  include_refunds: "Include refund amounts and refund counts in KPIs and breakdowns.",
  include_voided: "Include void/voided tickets in addition to closed/refunded statuses.",
};

const HELP_GUIDE = buildSuperAdminReportHelpGuide({
  reportKey: "kpis",
  title: "System KPI Snapshot (Super Admin)",
  subtitle:
    "Run system-wide KPIs across the selected scope with timezone-aware accounting dates.",
  summaryLabel: "KPI Summary",
  detailsLabel: "Breakdowns",
  hasExport: true,
});

const HELP_ANCHORS = getSuperAdminHelpAnchors("kpis");

function toMoney(value, currency = "CAD") {
  const numeric = Number(value || 0);
  const amount = Number.isFinite(numeric) ? numeric : 0;
  return new Intl.NumberFormat("en-CA", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function toCount(value) {
  const numeric = Number(value || 0);
  const count = Number.isFinite(numeric) ? numeric : 0;
  return count.toLocaleString("en-CA");
}

function toMoneyPerMinute(value, currency = "CAD") {
  return `${toMoney(value, currency)}/min`;
}

function csvEscape(value) {
  const text = String(value ?? "");
  if (text.includes(",") || text.includes("\n") || text.includes("\"")) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

function buildReportCsv(report) {
  const lines = [];
  lines.push(["Scope", report?.scope || ""].map(csvEscape).join(","));
  lines.push(
    [
      "Date Range",
      `${report?.date_range?.start || ""} to ${report?.date_range?.end || ""}`,
    ]
      .map(csvEscape)
      .join(",")
  );
  lines.push("");

  lines.push("Summary");
  lines.push(["Metric", "Value"].join(","));
  Object.entries(report?.summary || {}).forEach(([key, value]) => {
    lines.push([key, value].map(csvEscape).join(","));
  });
  lines.push("");

  lines.push("By Salon");
  lines.push(["Salon ID", "Salon", "Ticket Count", "Net Sales", "Tips", "Refunds"].join(","));
  (report?.breakdowns?.by_salon || []).forEach((row) => {
    lines.push(
      [
        row.salon_id,
        row.salon_name,
        row.ticket_count,
        row.net_sales,
        row.tips_total,
        row.refund_amount,
      ]
        .map(csvEscape)
        .join(",")
    );
  });
  lines.push("");

  lines.push("By Payment Method");
  lines.push(["Method", "Count", "Amount"].join(","));
  (report?.breakdowns?.by_payment_method || []).forEach((row) => {
    lines.push([row.method, row.count, row.amount].map(csvEscape).join(","));
  });
  lines.push("");

  lines.push("Series");
  lines.push(["Date", "Ticket Count", "Net Sales", "Tips", "Refunds"].join(","));
  (report?.series || []).forEach((row) => {
    lines.push(
      [row.date, row.ticket_count, row.net_sales, row.tips_total, row.refund_amount]
        .map(csvEscape)
        .join(",")
    );
  });

  return lines.join("\n");
}

function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

export default function SuperAdminReports() {
  const navigate = useNavigate();
  const { is_super_admin } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!is_super_admin) return;
    let mounted = true;

    const load = async () => {
      setLoadingFilters(true);
      setError("");
      try {
        const payload = await fetchSuperAdminReportFilters();
        if (!mounted) return;
        setBusinesses(Array.isArray(payload?.businesses) ? payload.businesses : []);
        setSalons(Array.isArray(payload?.salons) ? payload.salons : []);
      } catch (err) {
        if (!mounted) return;
        setError(err?.message || "Unable to load report filters.");
      } finally {
        if (mounted) {
          setLoadingFilters(false);
        }
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [is_super_admin]);

  const filteredSalons = useMemo(() => {
    if (!filters.business_id) return salons;
    return salons.filter(
      (salon) => String(salon.business_id || "") === String(filters.business_id)
    );
  }, [filters.business_id, salons]);

  const currency = report?.currency || "CAD";
  const summary = report?.summary || {};

  const renderFieldLabel = (label, key) => (
    <span className="inline-flex items-center gap-2">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </span>
      {KPI_TOOLTIPS[key] ? <InfoTooltip text={KPI_TOOLTIPS[key]} label={label} /> : null}
    </span>
  );

  const handleClose = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    navigate("/superadmin/reports");
  };

  const setFilter = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "scope") {
        if (value === "all") {
          next.business_id = "";
          next.salon_id = "";
          next.group_by = "salon";
        }
        if (value === "business") {
          next.salon_id = "";
          if (next.group_by !== "day") {
            next.group_by = "salon";
          }
        }
        if (value === "salon") {
          next.group_by = "day";
        }
      }
      if (key === "business_id" && next.salon_id) {
        const selectedSalon = salons.find(
          (salon) => String(salon.id) === String(next.salon_id)
        );
        if (selectedSalon && String(selectedSalon.business_id) !== String(value || "")) {
          next.salon_id = "";
        }
      }
      return next;
    });
  };

  const runReport = async () => {
    if (filters.scope === "business" && !filters.business_id) {
      setError("Choose a business for business scope.");
      return;
    }
    if (filters.scope === "salon" && !filters.salon_id) {
      setError("Choose a salon for salon scope.");
      return;
    }
    if (filters.preset === "custom" && !filters.start_date && !filters.end_date) {
      setError("Provide a start or end date for custom range.");
      return;
    }

    setError("");
    setLoadingReport(true);
    try {
      const payload = await fetchSuperAdminReportKpis({
        scope: filters.scope,
        business_id: filters.business_id || undefined,
        salon_id: filters.salon_id || undefined,
        preset: filters.preset === "custom" ? undefined : filters.preset,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
        group_by: filters.scope === "salon" ? "day" : filters.group_by,
        include_refunds: filters.include_refunds,
        include_voided: filters.include_voided,
      });
      setReport(payload || null);
    } catch (err) {
      if (err?.status === 401 || err?.status === 403) {
        setError("Super Admin access required.");
      } else {
        setError(err?.message || "Unable to run report.");
      }
      setReport(null);
    } finally {
      setLoadingReport(false);
    }
  };

  const exportCsv = () => {
    if (!report) return;
    const csv = buildReportCsv(report);
    const now = new Date();
    const dateStamp = now.toISOString().slice(0, 10);
    downloadCsv(`superadmin-report-${dateStamp}.csv`, csv);
  };

  if (!is_super_admin) {
    return (
      <div className="reportSectionShell min-h-screen bg-slate-100">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <h1 className="text-xl md:text-2xl font-semibold text-white">System KPI Snapshot (Super Admin)</h1>
            <div
              className="absolute right-4 top-3 z-50"
              onClick={handleClose}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <ModalCloseX onClick={handleClose} />
            </div>
          </div>

          <div className="px-6 py-6 space-y-6">
            <div
              id={HELP_ANCHORS.overview}
              className={`rounded-2xl ${getSectionClassName(HELP_ANCHORS.overview)}`}
            >
              <ReportHelpCard
                title={HELP_GUIDE.title}
                subtitle={HELP_GUIDE.subtitle}
                sections={HELP_GUIDE.sections}
                troubleshooting={HELP_GUIDE.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>
            {error ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}

            <div
              id={HELP_ANCHORS.filters}
              className={`space-y-4 rounded-2xl ${getSectionClassName(HELP_ANCHORS.filters)}`}
            >
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <h2 className="text-lg font-semibold text-slate-900">How Scope Works</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Run system-wide KPIs across all salons, salons under one business, or one salon.
                  Accounting dates use each salon/business timezone, not browser or UTC time.
                </p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 lg:p-6 space-y-4">
                <h2 className="text-lg md:text-xl font-semibold text-slate-900">Filters</h2>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                <div>
                  {renderFieldLabel("Scope", "scope")}
                  <select
                    value={filters.scope}
                    onChange={(event) => setFilter("scope", event.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  {renderFieldLabel("Business", "business")}
                  <select
                    value={filters.business_id}
                    disabled={filters.scope === "all" || loadingFilters}
                    onChange={(event) => setFilter("business_id", event.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:bg-slate-100"
                  >
                    <option value="">All Businesses</option>
                    {businesses.map((business) => (
                      <option key={business.id} value={business.id}>
                        {business.code} - {business.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  {renderFieldLabel("Salon", "salon")}
                  <select
                    value={filters.salon_id}
                    disabled={filters.scope !== "salon" || loadingFilters}
                    onChange={(event) => setFilter("salon_id", event.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:bg-slate-100"
                  >
                    <option value="">Select Salon</option>
                    {filteredSalons.map((salon) => (
                      <option key={salon.id} value={salon.id}>
                        {salon.name} (ID {salon.id})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  {renderFieldLabel("Date Preset", "preset")}
                  <select
                    value={filters.preset}
                    onChange={(event) => setFilter("preset", event.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  >
                    {PRESET_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={filters.start_date}
                    onChange={(event) => setFilter("start_date", event.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={filters.end_date}
                    onChange={(event) => setFilter("end_date", event.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  />
                </div>

                <div>
                  {renderFieldLabel("Group By", "group_by")}
                  <select
                    value={filters.scope === "salon" ? "day" : filters.group_by}
                    disabled={filters.scope === "salon"}
                    onChange={(event) => setFilter("group_by", event.target.value)}
                    className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 disabled:bg-slate-100"
                  >
                    {GROUP_BY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-5">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={filters.include_refunds}
                    onChange={(event) => setFilter("include_refunds", event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-slate-900"
                  />
                  Include refunds
                  <InfoTooltip text={KPI_TOOLTIPS.include_refunds} label="Include refunds" />
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={filters.include_voided}
                    onChange={(event) => setFilter("include_voided", event.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 text-slate-900"
                  />
                  Include voided
                  <InfoTooltip text={KPI_TOOLTIPS.include_voided} label="Include voided" />
                </label>
              </div>

                <div
                  id={HELP_ANCHORS.export}
                  className={`flex flex-wrap gap-2 rounded-2xl ${getSectionClassName(
                    HELP_ANCHORS.export
                  )}`}
                >
                  <button
                    type="button"
                    onClick={runReport}
                    disabled={loadingReport || loadingFilters}
                    className="rounded-full bg-slate-900 px-5 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
                  >
                    {loadingReport ? "Running..." : "Run Report"}
                  </button>
                  <button
                    type="button"
                    onClick={exportCsv}
                    disabled={!report}
                    className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
                  >
                    Export CSV
                  </button>
                </div>
              </div>
            </div>

            {report ? (
              <>
                <div
                  id={HELP_ANCHORS.kpis}
                  className={`rounded-2xl ${getSectionClassName(HELP_ANCHORS.kpis)}`}
                >
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    {KPI_CARDS.map((card) => (
                      <div
                        key={card.key}
                        className="rounded-2xl border border-slate-200 bg-white p-4"
                      >
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {card.label}
                        </div>
                        <div className="mt-2 text-2xl font-semibold text-slate-900">
                          {card.type === "money"
                            ? toMoney(summary?.[card.key], currency)
                            : card.type === "money_per_minute"
                              ? toMoneyPerMinute(summary?.[card.key], currency)
                              : toCount(summary?.[card.key])}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  id={HELP_ANCHORS.details}
                  className={`space-y-4 rounded-2xl ${getSectionClassName(HELP_ANCHORS.details)}`}
                >
                  <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <h3 className="text-base font-semibold text-slate-900">By Salon</h3>
                      <div className="mt-3 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                            <tr>
                              <th className="px-3 py-2 text-left">Salon</th>
                              <th className="px-3 py-2 text-right">Ticket Count</th>
                              <th className="px-3 py-2 text-right">Net Sales</th>
                              <th className="px-3 py-2 text-right">Tips</th>
                              <th className="px-3 py-2 text-right">Refunds</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {(report?.breakdowns?.by_salon || []).length === 0 ? (
                              <tr>
                                <td className="px-3 py-3 text-slate-500" colSpan={5}>
                                  No salon rows for this range.
                                </td>
                              </tr>
                            ) : (
                              (report?.breakdowns?.by_salon || []).map((row) => (
                                <tr key={`salon-${row.salon_id}`}>
                                  <td className="px-3 py-2 text-slate-800">{row.salon_name}</td>
                                  <td className="px-3 py-2 text-right text-slate-700">
                                    {toCount(row.ticket_count)}
                                  </td>
                                  <td className="px-3 py-2 text-right text-slate-700">
                                    {toMoney(row.net_sales, currency)}
                                  </td>
                                  <td className="px-3 py-2 text-right text-slate-700">
                                    {toMoney(row.tips_total, currency)}
                                  </td>
                                  <td className="px-3 py-2 text-right text-slate-700">
                                    {toMoney(row.refund_amount, currency)}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <h3 className="text-base font-semibold text-slate-900">By Payment Method</h3>
                      <div className="mt-3 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                            <tr>
                              <th className="px-3 py-2 text-left">Method</th>
                              <th className="px-3 py-2 text-right">Count</th>
                              <th className="px-3 py-2 text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {(report?.breakdowns?.by_payment_method || []).length === 0 ? (
                              <tr>
                                <td className="px-3 py-3 text-slate-500" colSpan={3}>
                                  No payment rows for this range.
                                </td>
                              </tr>
                            ) : (
                              (report?.breakdowns?.by_payment_method || []).map((row) => (
                                <tr key={`method-${row.method}`}>
                                  <td className="px-3 py-2 text-slate-800">{row.method}</td>
                                  <td className="px-3 py-2 text-right text-slate-700">
                                    {toCount(row.count)}
                                  </td>
                                  <td className="px-3 py-2 text-right text-slate-700">
                                    {toMoney(row.amount, currency)}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {(report?.series || []).length > 0 ? (
                    <div className="rounded-2xl border border-slate-200 bg-white p-4">
                      <h3 className="text-base font-semibold text-slate-900">Daily Series</h3>
                      <div className="mt-3 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                            <tr>
                              <th className="px-3 py-2 text-left">Date</th>
                              <th className="px-3 py-2 text-right">Ticket Count</th>
                              <th className="px-3 py-2 text-right">Net Sales</th>
                              <th className="px-3 py-2 text-right">Tips</th>
                              <th className="px-3 py-2 text-right">Refunds</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-200">
                            {(report?.series || []).map((row) => (
                              <tr key={`series-${row.date}`}>
                                <td className="px-3 py-2 text-slate-800">{row.date}</td>
                                <td className="px-3 py-2 text-right text-slate-700">
                                  {toCount(row.ticket_count)}
                                </td>
                                <td className="px-3 py-2 text-right text-slate-700">
                                  {toMoney(row.net_sales, currency)}
                                </td>
                                <td className="px-3 py-2 text-right text-slate-700">
                                  {toMoney(row.tips_total, currency)}
                                </td>
                                <td className="px-3 py-2 text-right text-slate-700">
                                  {toMoney(row.refund_amount, currency)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : null}
                </div>
              </>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-600">
                {loadingReport
                  ? "Running report..."
                  : "Choose filters, then run report to view KPI metrics."}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}
